/**
 * Single-source-of-truth currency formatter.
 *
 * Defaults to 4 decimals per Peter's R12 #7 request — the legacy blotter
 * displays dollar amounts at 4-decimal precision (e.g. $0.0125) and the new
 * system must match so per-row comparisons line up. The backend already
 * stores commissions at 4-decimal precision; rounding to 2 in the UI hid
 * fractional-cent values like $0.0125 → $0.01.
 *
 * Pass `{ precision: 2 }` at a call site only when 2 decimals are explicitly
 * required (rare — consider whether you really need 2 before overriding).
 */
export interface CurrencyFormatOptions {
  /** Number of fractional digits to render. Defaults to 4. */
  precision?: number;
  /** Currency symbol prefix. Defaults to "$". */
  symbol?: string;
}

/**
 * Format a numeric (or numeric-string) amount as a currency string.
 * Returns `'$0.0000'` (or the fallback at the chosen precision) for null,
 * undefined, NaN, or otherwise non-finite input.
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  options: CurrencyFormatOptions = {}
): string => {
  const precision = options.precision ?? 4;
  const symbol = options.symbol ?? '$';

  const numeric =
    typeof amount === 'string' ? parseFloat(amount) : (amount as number);
  const value = Number.isFinite(numeric) ? (numeric as number) : 0;

  // Intl.NumberFormat with `style: 'currency'` always prefixes its currency
  // glyph. To respect a caller-supplied `symbol`, we format the number alone
  // and prepend the symbol ourselves — preserving locale thousand separators
  // (e.g. "1,234.5600") while still letting the symbol be customized.
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value);

  return `${symbol}${formatted}`;
};
